<template>
  <div
    class="tito-ticket-details-question"
    v-bind:class="{ 'invalid-field': !!error }"
  >
    <label
      class="tito-ticket-details-question--label"
      :for="'tito-question' + question.id"
      >{{ title }}
      <span v-if="question.required" class="tito-required"> *</span></label
    >
    <div
      class="tito-ticket-details-question--description markdown"
      v-if="question.description"
      v-html="description"
    />

    <input
      v-if="is('Text')"
      v-model="primary_response"
      type="text"
      class="tito-tickets-details-question--form-control"
      :id="'tito-question' + question.id"
      :name="name"
      v-validation="{ required: question.required }"
    />

    <textarea
      v-else-if="is('Textarea')"
      v-model="primary_response"
      class="tito-tickets-details-question--form-control"
      ref="textarea"
      type="text"
      :id="'tito-question' + question.id"
      :name="name"
      v-validation="{ required: question.required }"
    ></textarea>

    <template v-else-if="is('Checkboxes')">
      <label
        v-for="option in options"
        class="tito-ticket-details-question--checkbox"
      >
        <input v-model="primary_response" type="checkbox" :value="option" />
        {{ option }}
      </label>
    </template>

    <searchable-select
      v-else-if="is('Select')"
      :id="'tito-question' + question.id"
      v-model="primary_response"
      :options="selectOptions"
      :placeholder="$t('searchable_select.placeholder', this.locale)"
    />

    <country-select
      v-else-if="is('Country')"
      :id="'tito-question' + question.id"
      v-model="primary_response"
      :priority-countries="options"
      :name="name"
      :required="question.required"
      :locale="locale"
    ></country-select>

    <template v-else-if="is('File')">
      <template v-if="filename && !uploading">
        <span v-if="filename">{{ filename }}</span>
        <input v-model="remote_file_url" type="hidden" />
        <button
          type="button"
          class="btn-link"
          @click.prevent="uploading = true"
          v-if="filename"
        >
          {{ $t("questions.change_file", locale) }}
        </button>
      </template>
      <tito-uploader
        :locale="locale"
        :key="`question-${question.id}`"
        :question="question"
        :ticket="ticket"
        v-else
        @uploadComplete="onUploadComplete"
      ></tito-uploader>
    </template>

    <div
      class="tito-ticket-details-question--alternate-response"
      v-if="question.include_free_text_field"
    >
      <label
        class="tito-ticket-details-question--label"
        :for="'tito-question' + question.id + '-alternate-response'"
        >{{ optionsFreeTextField }}</label
      >
      <input
        v-model="alternate_response"
        type="text"
        class="tito-tickets-details-question--form-control"
        :id="'tito-question' + question.id + '-alternate-response'"
        :name="name"
      />
    </div>

    <span class="tito-error" v-if="error">
      {{ error }}
    </span>
  </div>
</template>

<style scoped>
button.btn-link {
  border: none;
  background: none;
}
</style>

<script>
import autosize from "autosize";
const TitoUploader = () => ({
  component: import("./TitoUploader.vue"),
});
import { Drop } from "vue-drag-drop";

import CountrySelect from "./CountrySelect.vue";
import SearchableSelect from "application/components/shared/SearchableSelect.vue";

import translate from "js/src/mixins/translate.js";
import validation from "js/src/directives/validation.js";

export default {
  directives: { validation },
  components: { CountrySelect, Drop, SearchableSelect, TitoUploader },
  mixins: [translate],
  props: ["question", "value", "locale", "errors", "ticket"],

  data() {
    return {
      answer_id: this.value.id,
      primary_response: this.initialPrimaryResponse(),
      alternate_response: this.value.alternate_response,
      filename: this.initialFilename(),
      uploading: false,
      remote_file_url: null,
    };
  },

  mounted() {
    autosize(this.$refs.textarea);
  },

  watch: {
    alternate_response() {
      this.emitValue();
    },

    primary_response() {
      this.emitValue();
    },

    remote_file_url() {
      this.emitValue();
    },
  },

  computed: {
    description() {
      return this.translate(this.question, "description");
    },

    error() {
      if (this.errors && this.errors.questions) {
        const error = this.errors.questions.find((error) => {
          return !!error[this.question.id];
        });
        if (error) {
          return error[this.question.id];
        }
      }
    },

    selectOptions() {
      return this.options.map((option) => {
        return { value: option, label: option };
      });
    },

    options() {
      return (this.translate(this.question, "options") || "")
        .split("\n")
        .map((item) => item.trim());
    },

    optionsFreeTextField() {
      return this.translate(this.question, "options_free_text_field");
    },

    name() {
      if (this.question.slug) {
        return this.question.slug;
      }
      return "question-#{this.question.id}";
    },

    title() {
      return this.translate(this.question, "title");
    },
  },

  methods: {
    defaultPrimaryResponse() {
      return this.is("Checkboxes") ? [] : "";
    },

    is(type) {
      return this.question.field_type == type;
    },

    emitValue() {
      let out = {
        id: this.answer_id,
        question_id: this.question.id,
      };
      if (!this.is("File")) {
        // Unselecting a multi-select sets value to null, convert it to empty string or answer can't be removed
        out["primary_response"] =
          this.primary_response == null
            ? this.defaultPrimaryResponse()
            : this.primary_response;

        if (this.question.include_free_text_field) {
          out["alternate_response"] = this.alternate_response;
        }
      } else if (this.remote_file_url) {
        out["remote_file_url"] = this.remote_file_url;
      }

      this.$emit("input", out);
    },

    initialFilename() {
      if (this.is("File")) return this.value.response;
    },

    initialPrimaryResponse() {
      return this.value.primary_response || this.defaultPrimaryResponse();
    },

    onUploadComplete(upload) {
      this.uploading = false;
      this.filename = upload.name;
      this.remote_file_url = upload.uploadURL;
    },

    handleDrag() {
      this.uploading = true;
    },
  },
};
</script>
